import {Injectable} from '@angular/core';
import {AnyAdapter} from '@drl/core/api/adapters/any-adapter.service';
import {ApiService} from '@drl/core/api/api.service';
import {FetchAlertsCommand} from '../../shared/commands/fetch-alerts.command';
import {Alerts} from '@drl/shared/models/alerts.model';
import {HttpParams} from '@angular/common/http';
import {AlertStatus, alertLogStatusMap} from '../../shared/utility/alerts.enum';
import {UpdateAlertsCommand} from './shared/commands/update-alerts.command';
import {FetchAuditTriggerLogsCommand} from './shared/commands/fetch-audit-trigger-logs.command';
import {AuditTriggerLogs} from '@drl/shared/models/audit-trigger-logs.model';
import {AlertsAdapter} from '@drl/shared/adapters/alerts-adapter.service';
import {AlertAuditLogAdapter} from './shared/adapters/alert-audit-trigger-logs.adapter';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {UserFacadeService} from '@drl/shared/facades';
import {keyBy, uniq} from 'lodash';
import {FetchAlertsCountCommand} from '@drl/shared/commands/fetch-alerts-count.command';
import {Count} from '@drl/core/models';
import {CountAdapter} from '@drl/core/api/adapters/count-adapter.service';
import {FetchAlertByIdCommand} from '@drl/shared/commands/fetch-alert-by-id.command';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {FetchSideEffectLogsCommand} from '@drl/shared/commands';
import {SideEffectLogs} from './shared/models';
import * as moment from 'moment';

const sortOrderDesc = 'createdOn DESC';

@Injectable({
  providedIn: 'root',
})
export class AlertFacadeService {
  constructor(
    private readonly apiService: ApiService,
    private readonly anyAdapter: AnyAdapter,
    private readonly alertsAdapter: AlertsAdapter,
    private readonly alertLogAdapter: AlertAuditLogAdapter,
    private readonly userFacade: UserFacadeService,
    private readonly countAdapter: CountAdapter,
    private readonly translateSvc: IpsTranslateService,
  ) {}
  alertLogStatus = alertLogStatusMap(this.translateSvc.translate);
  fetchAlertsCount(
    patientId: number,
    priority: number,
    status: AlertStatus,
    assignee: number,
    startDate,
    endDate,
  ): Observable<Count> {
    const command: FetchAlertsCountCommand<Count> = new FetchAlertsCountCommand(
      this.apiService,
      this.countAdapter,
    );

    const queryData = {
      patientId: undefined,
      priority: undefined,
      status: undefined,
      assignee: undefined,
      createdOn: undefined,
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (priority) {
      queryData.priority = priority;
    }

    if (status) {
      queryData.status = status;
    }

    if (assignee) {
      queryData.assignee = assignee;
    }

    if (startDate && endDate) {
      queryData.createdOn = {
        between: [startDate, endDate],
      };
    }

    let params = new HttpParams();
    params = params.set('where', JSON.stringify(queryData));

    command.parameters = {
      query: params,
    };
    return command.execute();
  }

  fetchAlerts(
    patientId: number,
    priority: number,
    status: AlertStatus,
    assignee: number,
    startDate: number,
    endDate: number,
  ): Observable<Alerts[]> {
    const command: FetchAlertsCommand<Alerts> = new FetchAlertsCommand(
      this.apiService,
      this.alertsAdapter,
    );
    const order = [sortOrderDesc];

    let queryData = {
      patientId: undefined,
      priority: undefined,
      status: undefined,
      assignee: undefined,
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (priority) {
      queryData.priority = priority;
    }

    if (status) {
      queryData.status = status;
    }

    if (assignee) {
      queryData.assignee = assignee;

    }

    if(startDate && endDate){
      queryData['createdOn'] = {
        between: [new Date(startDate), new Date(endDate)],
      }
    }
    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });



    let params = new HttpParams();
    // params = params.set('start_date', startDate.toString());
    // params = params.set('end_date', endDate.toString());
    params = params.set('filter', queryStr);

    command.parameters = {
      query: params,
    };
    return command.execute();
  }

  fetchAlertById(id: number) {
    const command: FetchAlertByIdCommand<Alerts> = new FetchAlertByIdCommand(
      this.apiService,
      this.alertsAdapter,
      id,
    );

    return command.execute();
  }

  getSideEffectLogs(patientId: number, startDate?: Date, endDate?: Date) {
    const command: FetchSideEffectLogsCommand<SideEffectLogs> = new FetchSideEffectLogsCommand(
      this.apiService,
      this.anyAdapter,
    );

    const sortOrderAsc = 'logDate DESC';

    const order = [sortOrderAsc];
    const queryData = {
      patientId: undefined,
      and: [],
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (startDate) {
      queryData.and.push({
        createdOn: {
          gte: moment(startDate).startOf('day').toDate(),
        },
      });
    }

    if (endDate) {
      queryData.and.push({
        createdOn: {
          lte: moment(endDate).endOf('day').toDate(),
        },
      });
    }

    const queryStr = JSON.stringify({
      where: queryData,
      order,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };

    return command.execute();
  }
  updateAlertAssignee(patientId: number, assignee: number, alertId: number) {
    const command: UpdateAlertsCommand<
      Partial<Alerts>
    > = new UpdateAlertsCommand(this.apiService, this.anyAdapter, alertId);
    const queryData = {
      patientId: undefined,
    };
    if (patientId) {
      queryData.patientId = patientId;
    }

    const data = {
      assignee,
    };

    const queryStr = JSON.stringify(queryData);
    command.parameters = {
      data,
      query: new HttpParams().set('where', queryStr),
    };
    return command.execute();
  }

  updateAlertResolutionComment(
    patientId: number,
    comment: string,
    alertId: number,
  ) {
    const command: UpdateAlertsCommand<
      Partial<Alerts>
    > = new UpdateAlertsCommand(this.apiService, this.anyAdapter, alertId);
    const queryData = {
      patientId: undefined,
    };
    if (patientId) {
      queryData.patientId = patientId;
    }

    const data = {
      status: AlertStatus.Resolved,
      resolutionComment: comment,
    };

    const queryStr = JSON.stringify(queryData);
    command.parameters = {
      data,
      query: new HttpParams().set('where', queryStr),
    };
    return command.execute();
  }

  fetchAuditTriggerLogs(patientId: number, logType: string, entityId: string) {
    const command: FetchAuditTriggerLogsCommand<AuditTriggerLogs> = new FetchAuditTriggerLogsCommand(
      this.apiService,
      this.alertLogAdapter,
    );

    const queryData = {
      patientId: undefined,
      entityId: undefined,
      logType: undefined,
    };

    if (patientId) {
      queryData.patientId = patientId;
    }

    if (entityId) {
      queryData.entityId = entityId;
    }

    if (logType) {
      queryData.logType = logType;
    }

    const queryStr = JSON.stringify({
      where: queryData,
    });
    command.parameters = {
      query: new HttpParams().set('filter', queryStr),
    };
    return command.execute().pipe(
      tap(logs => {
        let userIds = logs.reduce((log: number[], next: AuditTriggerLogs) => {
          log.push(parseInt(next.userId.split('"')[1]));
          if (next.after && (next.after as Alerts).assignee) {
            log.push((next.after as Alerts).assignee);
          }
          return log;
        }, []);
        userIds = uniq(userIds).filter(id => !!id);
        this.userFacade
          .getUsers(0, null, null, null, null, null, null, null, null, userIds)
          .subscribe(users => {
            const userIdMap = keyBy(users, 'id');
            logs.forEach(log => {
              if (
                log.operationName ===
                (this.alertLogStatus.AlertResolved as string)
              ) {
                log.operationName += ` ${
                  userIdMap[log.userId.split('"')[1]].fullName
                }`;
              }

              if (
                log.operationName ===
                (this.alertLogStatus.AsigneeUpdated as string)
              ) {
                log.operationName += ` ${
                  userIdMap[(log.after as Alerts).assignee].fullName
                }`;
              }
            });
          });
      }),
    );
  }
}
